/**
 * PlayerFinancialsTabContainer — logic layer for the OWNER-only
 * Player Page Financials tab (SE-4348).
 *
 * Owns local UI state (selected document type + inner tab), invokes
 * {@link useContractExtraction} to load the saved extraction, and
 * handles the deep-link navigation to `/squad?playerId={id}` so the
 * contracts drawer auto-opens for the same player. The container
 * passes `readOnly` to the extraction section — the Financials tab is
 * explicitly a read-only surface per AC-5 of SE-4348; all mutations
 * happen inside the Squad > Contracts drawer.
 * @remarks
 * The `isOwner` prop is passed in by the caller (header container)
 * rather than read from a hook here so this component stays testable
 * without pulling in `useCurrentUser`. The header derives it from the
 * shared `currentUser.role.name === RoleName.Owner` check; once
 * {@link https://github.com/geminisportsai/frontend-v2/pull/1808 SE-4350
 * PR #1808} merges, callers should swap to
 * `useIsContractOwner()` for DRY-ness.
 * @module features/player-detail/components/PlayerFinancialsTab/PlayerFinancialsTabContainer
 */

import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import React, { memo, useCallback, useState } from "react";

import PlayerFinancialsTabView, {
  type FinancialsInnerTab,
} from "./PlayerFinancialsTabView";

import { useContractExtraction } from "@/features/contracts/hooks/useContractExtraction";
import type { ContractDocumentType } from "@/features/contracts/types";
import {
  CONTRACT_DRAWER_RETURN_CRUMB_LABEL,
  CONTRACT_DRAWER_RETURN_CRUMB_PATH,
  requestContractDrawerDeepLink,
} from "@/features/contracts/utils/contractDrawerDeepLinkRequests";
import { useNavigationContext } from "@/providers/NavigationProvider";

/** Props accepted by the Financials tab container. */
export interface PlayerFinancialsTabContainerProps {
  /** Player UUID the tab is rendering for. */
  readonly playerId: string;
  /** Whether the current user is an OWNER (gates the Edit button). */
  readonly isOwner: boolean;
  /**
   * Display name of the player, used when the user clicks "Edit in
   * Contracts" so the drawer header has the right title before the
   * organization roster query lands.
   */
  readonly playerName?: string | null;
}

/**
 * Container for the Financials tab.
 * @param props - Container props
 * @param props.playerId - Player UUID
 * @param props.isOwner - Whether the current user is an OWNER
 * @param props.playerName - Display name threaded into the drawer deep-link
 * @returns The Financials tab view bound to live extraction state
 */
function PlayerFinancialsTabContainer({
  playerId,
  isOwner,
  playerName,
}: PlayerFinancialsTabContainerProps) {
  const router = useRouter();
  const { clearBreadcrumbs, pushBreadcrumb } = useNavigationContext();

  const [documentType, setDocumentType] =
    useState<ContractDocumentType>("CONTRACT");
  const [innerTab, setInnerTab] = useState<FinancialsInnerTab>("contracts");

  const { extraction } = useContractExtraction(playerId, documentType);

  const handleDocumentTypeChange = useCallback((next: string) => {
    // Cast is safe — Select only yields values we registered
    // (CONTRACT, TRANSFER_AGREEMENT). Anything else is dropped.
    if (next === "CONTRACT" || next === "TRANSFER_AGREEMENT") {
      setDocumentType(next);
    }
  }, []);

  const handlePressContractsTab = useCallback(() => {
    setInnerTab("contracts");
  }, []);

  const handlePressLiveTrackingTab = useCallback(() => {
    setInnerTab("liveTracking");
  }, []);

  const handleEditInContractsPress = useCallback(() => {
    // SE-7737: record this click as a tokened request BEFORE navigating.
    // The Squad tab screen never unmounts, so two clicks on the same player
    // reach it as byte-identical params — the URL alone cannot express "the
    // user asked again". The request is consumed exactly once by
    // SquadScreenContainer, which is what makes the button keep working
    // after its first use.
    requestContractDrawerDeepLink({ playerId, playerName });
    // SE-7738: leave a trail back to this player. Squad renders the shared
    // breadcrumb when the stack ends here, so cancelling the drawer — or
    // uploading nothing — still leaves a one-tap route to the profile instead
    // of forcing the user to search the name again. This is the same
    // NavigationProvider mechanism `ContractDrawerHeaderContainer` uses for
    // the opposite direction (Squad → player), run the other way round.
    // `clearBreadcrumbs` first is what keeps repeated presses — possible
    // since SE-7737 — from stacking duplicate crumbs.
    clearBreadcrumbs();
    pushBreadcrumb({
      label: playerName ?? "Player",
      path: `/players/${playerId}`,
    });
    pushBreadcrumb({
      label: CONTRACT_DRAWER_RETURN_CRUMB_LABEL,
      path: CONTRACT_DRAWER_RETURN_CRUMB_PATH,
    });
    // The query params stay: they are what a full reload or a shared link
    // has to work from, and SquadScreenContainer still honors them.
    const nameParam = playerName
      ? `&playerName=${encodeURIComponent(playerName)}`
      : "";
    router.push(
      `/squad?playerId=${encodeURIComponent(playerId)}${nameParam}` as Href
    );
  }, [router, playerId, playerName, clearBreadcrumbs, pushBreadcrumb]);

  return (
    <PlayerFinancialsTabView
      playerId={playerId}
      isOwner={isOwner}
      documentType={documentType}
      innerTab={innerTab}
      extraction={extraction}
      onDocumentTypeChange={handleDocumentTypeChange}
      onPressContractsTab={handlePressContractsTab}
      onPressLiveTrackingTab={handlePressLiveTrackingTab}
      onEditInContractsPress={handleEditInContractsPress}
    />
  );
}

PlayerFinancialsTabContainer.displayName = "PlayerFinancialsTabContainer";

export default memo(PlayerFinancialsTabContainer);
